import React, { useState, useEffect } from 'react';
import { 
  Search, 
  X, 
  Calendar as CalendarIcon, 
  Clock, 
  User, 
  CheckCircle2,
  ChevronRight,
  Filter,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { format, addDays, startOfDay } from 'date-fns';
import { es } from 'date-fns/locale';

const FreeSlotFinder = ({ isOpen, onClose, onSelectSlot, currentProfessional }) => {
  const [loading, setLoading] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [searchDays, setSearchDays] = useState(7);
  const [selectedProfessional, setSelectedProfessional] = useState(currentProfessional || 'VER TODOS');

  const PROFESSIONALS = [
    'CONSULTORIOS EXTERNOS',
    'ECOGRAFIAS .',
    'FONOAUDIOLOGA .',
    'NEUMONOLOGO .',
    'NEUROLOGO .',
    'OTORRINO .',
    'PSICOLOGIA .',
    'RAYOS X'
  ];

  const findSlots = async () => {
    setLoading(true);
    try {
      const slots = [];
      const today = startOfDay(new Date());
      
      // Get all appointments for the next X days
      const endDate = addDays(today, searchDays);
      const { data: appointments, error } = await supabase
        .from('appointments')
        .select('appointment_date, appointment_time, professional_name')
        .gte('appointment_date', format(today, 'yyyy-MM-dd'))
        .lte('appointment_date', format(endDate, 'yyyy-MM-dd'));

      if (error) throw error;

      // Logic: Scan 8am to 8pm every 15 mins
      const professionalsToScan = selectedProfessional === 'VER TODOS' ? PROFESSIONALS : [selectedProfessional];
      
      for (let i = 0; i < searchDays; i++) {
        const scanDate = addDays(today, i);
        const dateStr = format(scanDate, 'yyyy-MM-dd');
        
        // Skip Sundays
        if (scanDate.getDay() === 0) continue;

        for (const prof of professionalsToScan) {
          for (let h = 8; h < 20; h++) {
            for (let m = 0; m < 60; m += 15) {
              const timeStr = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:00`;
              
              const isOccupied = appointments.some(a => 
                a.appointment_date === dateStr && 
                a.appointment_time?.startsWith(timeStr.substring(0, 5)) &&
                a.professional_name === prof
              );

              if (!isOccupied) {
                slots.push({
                  date: dateStr,
                  time: timeStr.substring(0, 5),
                  professional: prof,
                  dayName: format(scanDate, 'EEEE d', { locale: es })
                });
                if (slots.length >= 15) break; // Limit results
              }
            }
            if (slots.length >= 15) break;
          }
          if (slots.length >= 15) break;
        }
        if (slots.length >= 15) break;
      }
      setAvailableSlots(slots);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      Promise.resolve().then(() => {
        findSlots();
      });
    }
  }, [isOpen, selectedProfessional, searchDays]);

  if (!isOpen) return null;

  return (
    <div className="appointment-modal-overlay">
      <div className="appointment-modal-container glass-modal-premium" style={{ maxWidth: '600px' }}>
        <div className="appointment-modal-header premium-header">
          <div className="header-title">
            <div className="title-icon-wrapper green"><Search size={20} /></div>
            <span>BUSCADOR INTELIGENTE DE TURNOS LIBRES</span>
          </div>
          <button className="btn-close-modal" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="appointment-modal-body">
          <div className="finder-filters" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
            <div className="form-group">
              <label>Profesional / Especialidad</label>
              <select value={selectedProfessional} onChange={(e) => setSelectedProfessional(e.target.value)}>
                <option value="VER TODOS">-- TODOS LOS PROFESIONALES --</option>
                {PROFESSIONALS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Rango de Búsqueda</label>
              <select value={searchDays} onChange={(e) => setSearchDays(parseInt(e.target.value))}>
                <option value={7}>Próximos 7 días</option>
                <option value={15}>Próximos 15 días</option>
                <option value={30}>Próximo Mes</option>
              </select>
            </div>
          </div>

          <div className="available-slots-container" style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {loading ? (
              <div className="loading-slots text-center-p" style={{ padding: '2rem' }}>
                <RefreshCw size={24} className="animate-spin" style={{ marginBottom: '1rem', opacity: 0.5 }} />
                <p>Escaneando agendas disponibles...</p>
              </div>
            ) : availableSlots.length === 0 ? (
              <div className="no-slots-found text-center-p" style={{ padding: '2rem' }}>
                <AlertCircle size={32} style={{ color: '#ff5252', marginBottom: '1rem' }} />
                <p>No se encontraron turnos libres en el rango seleccionado.</p>
              </div>
            ) : (
              <div className="slots-list-premium" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {availableSlots.map((slot, idx) => (
                  <div 
                    key={idx} 
                    className="free-slot-item card glass-panel" 
                    onClick={() => onSelectSlot(slot)}
                    style={{ cursor: 'pointer', transition: 'all 0.2s', padding: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                      <div className="slot-date-badge" style={{ background: 'rgba(0, 229, 255, 0.1)', color: '#00e5ff', padding: '0.5rem', borderRadius: '8px', textAlign: 'center', minWidth: '100px' }}>
                        <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: '800' }}>{slot.dayName.split(' ')[0]}</div>
                        <div style={{ fontSize: '1.1rem', fontWeight: '800' }}>{slot.dayName.split(' ')[1]}</div>
                      </div>
                      <div>
                        <div style={{ fontWeight: '700', fontSize: '1.1rem', color: 'white' }}>{slot.time} hs</div>
                        <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase' }}>{slot.professional}</div>
                      </div>
                    </div>
                    <div className="slot-select-icon" style={{ color: '#00ff85' }}>
                      <ChevronRight size={20} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="appointment-modal-footer">
          <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)', marginRight: 'auto' }}>
            * Mostrando los primeros 15 resultados disponibles.
          </p>
          <button className="btn-cancel-modal" onClick={onClose}>CERRAR</button>
        </div>
      </div>

      <style>{`
        .free-slot-item:hover {
          transform: translateX(10px);
          background: rgba(255, 255, 255, 0.05);
          border-color: rgba(0, 229, 255, 0.3);
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default FreeSlotFinder;
